import dayjs from "dayjs";

export const NOTIFICATION_REFRESH_EVENT = "notification-center-refresh";
export const NOTIFICATION_DEGRADED_EVENT = "notification-center-degraded";
export const TG_ANALYSIS_REFRESH_EVENT = "tg-analysis-refresh";
export const TASK_REWARD_EVENT_TYPE = "ASSET_TASK_REWARD_GRANTED";

export function dispatchNotificationRefresh(reason = "update") {
  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_REFRESH_EVENT, { detail: { reason } }),
  );
}

export function dispatchNotificationDegraded(degraded, reason = "update") {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_DEGRADED_EVENT, {
      detail: { degraded: Boolean(degraded), reason },
    }),
  );
}

export function dispatchTgAnalysisRefresh(address) {
  if (!address || typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(TG_ANALYSIS_REFRESH_EVENT, { detail: { address } }),
  );
}

export function isNotificationRead(notification) {
  return (
    Boolean(notification && notification.readAt) ||
    Number(notification && notification.status) > 0
  );
}

export function isTaskRewardNotification(notification = {}) {
  return notification.eventType === TASK_REWARD_EVENT_TYPE;
}

export function formatNotificationTime(value) {
  const time = dayjs(value);
  if (!time.isValid()) return "";

  const now = dayjs();
  const minutes = now.diff(time, "minute");
  if (minutes < 1) return "刚刚";
  if (minutes < 60) return `${minutes}分钟前`;

  const hours = now.diff(time, "hour");
  if (hours < 24) return `${hours}小时前`;
  if (hours < 48) return `昨天 ${time.format("HH:mm")}`;
  return time.format("YYYY-MM-DD HH:mm");
}

// 地址一致性比较。
// EVM 地址（0x + 十六进制）的大小写只是 EIP-55 校验和，同一个地址会出现多种大小写写法
// （用户输入、collect 回显、通知 bizPayload 里的 address 可能各不相同），必须忽略大小写；
// TRON/BTC 等 base58 地址大小写敏感，只能严格比较，不能统一转小写。
export function isSameAddress(left, right) {
  const a = String(left == null ? "" : left).trim();
  const b = String(right == null ? "" : right).trim();
  if (!a || !b) return false;
  if (a === b) return true;
  const isHex = (value) => /^0x[0-9a-fA-F]+$/.test(value);
  return (
    a.length === b.length &&
    isHex(a) &&
    isHex(b) &&
    a.toLowerCase() === b.toLowerCase()
  );
}

function shortenNotificationAddress(value) {
  const address = String(value || "").trim();
  if (address.length <= 10) return address;
  return `${address.slice(0, 5)}..${address.slice(-3)}`;
}

function getNotificationAddress(notification) {
  const payload = parseNotificationBizPayload(notification.bizPayload);
  if (payload.address || payload.addressKey) {
    return payload.address || payload.addressKey;
  }

  const routeMatch = String(notification.route || "").match(
    /^\/tg\/address\/(.+)$/,
  );
  if (!routeMatch) return "";
  try {
    return decodeURIComponent(routeMatch[1]);
  } catch (error) {
    return routeMatch[1];
  }
}

function isTgTaskNotification(notification) {
  return (
    notification.eventType === "TG_TASK_FINISHED" ||
    (notification.category === "TG" && Boolean(notification.route))
  );
}

function replaceNotificationAddress(text, address, shortenedAddress) {
  if (!text || !address || !shortenedAddress || address === shortenedAddress) {
    return text || "";
  }
  return String(text).split(address).join(shortenedAddress);
}

function getNotificationAddressParts(text, address, shortenedAddress) {
  const value = String(text || "");
  const index = shortenedAddress ? value.indexOf(shortenedAddress) : -1;
  if (index < 0 || !address) {
    return null;
  }
  return {
    prefix: value.slice(0, index),
    address: shortenedAddress,
    suffix: value.slice(index + shortenedAddress.length),
    fullAddress: address,
  };
}

export function normalizeNotification(notification = {}, options = {}) {
  const isTgTask = isTgTaskNotification(notification);
  const isPreview = options.compact === true;
  const rawTitle = notification.title || "消息通知";
  const rawContent = notification.content || "";
  const fullAddress = getNotificationAddress(notification);
  const address = shortenNotificationAddress(fullAddress);
  const displayTitle = replaceNotificationAddress(
    rawTitle,
    fullAddress,
    address,
  );
  const displayContent = replaceNotificationAddress(
    rawContent,
    fullAddress,
    address,
  );
  const titleAddressParts = getNotificationAddressParts(
    displayTitle,
    fullAddress,
    address,
  );
  const contentAddressParts = getNotificationAddressParts(
    displayContent,
    fullAddress,
    address,
  );
  return {
    ...notification,
    title:
      isPreview && isTgTask
        ? "TG数据分析完成"
        : displayTitle,
    content: isPreview && isTgTask
      ? address
        ? `您的“${address}”地址TG公群舆情线索已分析完成`
        : "您的TG公群舆情线索已分析完成"
      : displayContent,
    titleAddressParts,
    contentAddressParts,
    isRead: isNotificationRead(notification),
    time: formatNotificationTime(notification.createdAt),
    type: String(notification.severity || "INFO").toLowerCase(),
    iconType: isTgTask ? "tg" : "system",
    actionText: isTgTask && notification.route ? "查看结果" : "",
  };
}

export function parseNotificationBizPayload(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
}

export function resolveNotificationRoute(notification = {}) {
  if (isTaskRewardNotification(notification)) {
    return { path: "/personalinfo", query: { focus: "tasks" } };
  }

  const route = notification.route;
  if (!route || !route.startsWith("/")) return null;

  if (route === "/tasks") {
    return { path: "/personalinfo", query: { focus: "tasks" } };
  }

  const tgAddressMatch = route.match(/^\/tg\/address\/(.+)$/);
  if (!tgAddressMatch) return route;

  const payload = parseNotificationBizPayload(notification.bizPayload);
  let address = payload.address || tgAddressMatch[1];
  try {
    address = decodeURIComponent(address);
  } catch (error) {
    // 保留后端原始地址，避免异常编码阻断跳转
  }
  return {
    path: "/toolpage/tgSentimentAnalysis",
    query: { address },
  };
}

export function getNotificationPageData(response, options = {}) {
  const page = response && response.code === 200 ? response.data : null;
  const normalizedMessages = Array.isArray(page && page.content)
    ? page.content.map((item) => normalizeNotification(item, options))
    : [];
  const hiddenMessages = normalizedMessages.filter(
    isTaskRewardNotification,
  );
  const includeTaskRewards = options.includeTaskRewards === true;
  const total = Number((page && page.totalElements) || 0);
  return {
    messages: includeTaskRewards
      ? normalizedMessages
      : normalizedMessages.filter(
          (item) => !isTaskRewardNotification(item),
        ),
    hiddenMessages,
    hiddenCount: hiddenMessages.length,
    hiddenUnreadCount: hiddenMessages.filter((item) => !item.isRead).length,
    total: includeTaskRewards
      ? total
      : Math.max(0, total - hiddenMessages.length),
    page: Number((page && page.number) || 0) + 1,
    size: Number((page && page.size) || 20),
  };
}
